import { useCallback, useEffect, useState } from "react";
import { LogOut, RefreshCw, Wallet } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, getApiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TransactionsTable } from "@/components/TransactionsTable";
import { StatementButton } from "@/components/StatementButton";

const Header = ({ user, onLogout }) => (
  <header className="border-b border-slate-200 bg-white">
    <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Wallet className="h-5 w-5 text-slate-900" />
        <span className="font-display text-lg font-bold text-slate-900">ThankF</span>
        <Badge variant="outline" className="ml-3 label-overline">Customer</Badge>
      </div>
      <div className="flex items-center gap-3">
        <span className="hidden sm:block text-sm text-slate-600">
          {user?.name} · <span className="font-mono-num">{user?.mobile}</span>
        </span>
        <Button variant="outline" size="sm" data-testid="customer-logout-btn" onClick={onLogout} className="gap-2">
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  </header>
);

const BalanceHero = ({ user, onRefresh }) => (
  <section className="bg-white border border-slate-200 rounded-lg p-8 md:p-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
    <div>
      <p className="label-overline text-slate-500">Current balance</p>
      <h1 data-testid="customer-balance-amount" className="font-mono-num text-5xl md:text-6xl font-semibold text-slate-900 mt-3 tracking-tight">
        {formatINR(user?.balance ?? 0)}
      </h1>
      <p className="text-slate-500 mt-2 text-sm">Last updated {formatDate(new Date())}</p>
    </div>
    <div className="flex gap-3">
      <StatementButton customer={user} endpoint="/customer/me/transactions" testid="customer-download-statement-btn" />
      <Button variant="outline" data-testid="customer-refresh-btn" onClick={onRefresh} className="gap-2">
        <RefreshCw className="h-4 w-4" /> Refresh
      </Button>
    </div>
  </section>
);

export default function CustomerDashboard() {
  const { user, logout, refreshMe } = useAuth();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await refreshMe();
      const { data } = await api.get("/customer/me/transactions");
      setTxns(data.transactions || []);
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setLoading(false);
    }
  }, [refreshMe]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} onLogout={logout} />
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10" data-testid="customer-dashboard">
        <BalanceHero user={user} onRefresh={load} />
        <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <p className="label-overline text-slate-500">Transaction history</p>
              <h2 className="font-display text-xl font-bold text-slate-900 mt-1">Recent activity</h2>
            </div>
            <Badge variant="outline" className="font-mono-num">{txns.length} entries</Badge>
          </div>
          <TransactionsTable txns={txns} loading={loading} testid="customer-transactions-table" />
        </section>
      </main>
    </div>
  );
}
